import { useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { motion, useAnimationControls } from 'motion/react';
import * as React from 'react';

import { useDictionary } from '@/core/providers/dictionary-provider';

function renderContentWithHighlights(
  content: string,
  highlights: string[] = [],
): React.ReactNode {
  if (!highlights || highlights.length === 0) {
    return content;
  }

  // Create a regex pattern that matches any of the highlight words (case-insensitive)
  const pattern = new RegExp(
    `(${highlights.map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
    'gi',
  );

  const parts: (string | React.ReactElement)[] = [];
  let lastIndex = 0;
  let match;

  // Reset regex lastIndex to ensure proper matching
  pattern.lastIndex = 0;

  while ((match = pattern.exec(content)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(content.substring(lastIndex, match.index));
    }

    // Add the highlighted match
    parts.push(
      <span
        key={match.index}
        className="font-semibold text-[color:var(--brand-600)] underline decoration-[color:var(--brand-600)] decoration-2 underline-offset-2"
      >
        {match[0]}
      </span>,
    );

    lastIndex = pattern.lastIndex;
  }

  // Add remaining text after the last match
  if (lastIndex < content.length) {
    parts.push(content.substring(lastIndex));
  }

  return parts.length > 0 ? parts : content;
}

export function WelcomeSlideshow() {
  const dict = useDictionary();
  const slides = dict.welcome.slides;
  const scrollControls = useAnimationControls();

  // Ref to measure the actual height of the slides container
  const containerRef = React.useRef<HTMLDivElement>(null);
  // Ref to the animated container to measure its height
  const animatedRef = React.useRef<HTMLDivElement>(null);
  const [totalHeight, setTotalHeight] = React.useState(0);

  const startScrollAnimation = React.useCallback(() => {
    if (totalHeight <= 0) {
      return;
    }
    void scrollControls.start({
      y: [0, -totalHeight],
      transition: {
        duration: slides.length * 5,
        repeat: Infinity,
        ease: 'linear',
        repeatType: 'loop',
        delay: 0.5,
      },
    });
  }, [scrollControls, slides.length, totalHeight]);

  // Measure height after component mounts and on changes
  React.useEffect(() => {
    const measureHeight = () => {
      // Try measuring from the animated container first (more reliable)
      if (animatedRef.current) {
        const firstHalf = animatedRef.current.children;
        if (firstHalf.length > 0) {
          let height = 0;
          // Sum up the height of the first set of slides (first half)
          for (let i = 0; i < slides.length; i++) {
            const child = firstHalf[i];
            if (child instanceof HTMLElement) {
              height += child.offsetHeight;
            }
          }
          // Add gap spacing (gap-4 = 1rem = 16px per gap, slides.length - 1 gaps)
          height += (slides.length - 1) * 16;
          // Add padding top (40px)
          height += 40;

          if (height > 0) {
            setTotalHeight(height);
            return;
          }
        }
      }

      // Fallback: measure from invisible container
      if (containerRef.current) {
        const firstSlideSet = containerRef.current.firstElementChild;
        if (firstSlideSet instanceof HTMLElement) {
          // Force a reflow to ensure accurate measurement
          void firstSlideSet.offsetHeight;
          const height = firstSlideSet.scrollHeight;
          if (height > 0) {
            setTotalHeight(height);
          }
        }
      }
    };

    // Initial measurement with multiple delays to ensure DOM is ready
    const timeoutId = setTimeout(measureHeight, 100);
    const timeoutId2 = setTimeout(measureHeight, 300);
    const timeoutId3 = setTimeout(measureHeight, 500);
    const timeoutId4 = setTimeout(measureHeight, 1000);

    // Re-measure on window resize
    const handleResize = () => {
      measureHeight();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      clearTimeout(timeoutId4);
      window.removeEventListener('resize', handleResize);
    };
  }, [slides]);

  React.useEffect(() => {
    startScrollAnimation();
  }, [startScrollAnimation]);

  useIonViewWillLeave(() => {
    void scrollControls.stop();
  });

  useIonViewWillEnter(() => {
    startScrollAnimation();
  });

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Top fade gradient */}
      <div className="pointer-events-none absolute top-0 z-10 h-24 w-full bg-gradient-to-b from-background to-transparent" />

      {/* Bottom fade gradient */}
      <div className="pointer-events-none absolute bottom-0 z-10 h-24 w-full bg-gradient-to-t from-background to-transparent" />

      <div
        ref={containerRef}
        className="invisible absolute"
        style={{
          visibility: 'hidden',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
        }}
      >
        {/* Render first set of slides to measure height */}
        <div className="flex flex-col gap-4" style={{ paddingTop: '40px' }}>
          {slides.map((slide, index) => (
            <div
              key={index}
              className="flex-shrink-0 rounded-[var(--r-card)] border border-border/80 bg-card p-5 shadow-[var(--shadow-card)] sm:rounded-[1.125rem] sm:p-6"
            >
              <div className="flex flex-col gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                  {slide.type === 'testimonial' ? 'Testimonial' : 'Feature'}
                </span>
                <p className="text-[13.5px] leading-relaxed text-card-foreground sm:text-base">
                  {renderContentWithHighlights(slide.content, slide.highlights)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <motion.div
        ref={animatedRef}
        animate={scrollControls}
        className="flex flex-col gap-4"
        style={{
          willChange: 'transform',
          paddingTop: '40px', // Add padding at the top (matches h-24 gradient height) so first item has space before fading
        }}
      >
        {/* Render slides twice for seamless loop */}
        {[...slides, ...slides].map((slide, index) => (
          <div
            key={index}
            className="flex-shrink-0 rounded-[var(--r-card)] border border-border/80 bg-card p-5 shadow-[var(--shadow-card)] sm:rounded-[1.125rem] sm:p-6"
          >
            <div className="flex flex-col gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                {slide.type === 'testimonial' ? 'Testimonial' : 'Feature'}
              </span>
              <p className="text-[13.5px] leading-relaxed text-card-foreground sm:text-base">
                {renderContentWithHighlights(slide.content, slide.highlights)}
              </p>
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}
